/**
 * Main Layout Component
 * Dynamics 365 Style - Top header bar with sidebar navigation
 */

import { type ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../contexts/I18nContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import {
  LayoutDashboard,
  Calendar,
  Ticket,
  Settings as SettingsIcon,
  Users,
  LogOut,
  Globe,
  Menu,
  X,
  ChevronRight,
  SlidersHorizontal,
  Building2,
  User,
} from 'lucide-react';

interface MainLayoutProps {
  children: ReactNode;
  /**
   * Focused forms with their own fixed action bar (the event wizard / edit page)
   * drop the phone's bottom nav: both sit at the bottom of the screen and the
   * nav covered 取消 / 下一步 / 儲存變更.
   */
  hideMobileNav?: boolean;
}

interface NavItem {
  path: string;
  labelKey: string;
  icon: React.ReactNode;
  roles?: string[];
  section?: 'main' | 'admin' | 'sysadmin';
  showForEventAdmin?: boolean;
}

const navItems: NavItem[] = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: <LayoutDashboard size={18} />, section: 'main' },
  { path: '/events', labelKey: 'nav.events', icon: <Calendar size={18} />, section: 'main' },
  { path: '/registrations', labelKey: 'nav.myRegistrations', icon: <Ticket size={18} />, section: 'main' },
  { path: '/settings', labelKey: 'nav.settings', icon: <SlidersHorizontal size={18} />, section: 'main' },
  { path: '/orgadmin/registrations', labelKey: 'nav.associationRegistrations', icon: <Users size={18} />, roles: ['OrgAdmin', 'SysAdmin'], section: 'admin', showForEventAdmin: true },
  { path: '/admin/events', labelKey: 'nav.eventManagement', icon: <SettingsIcon size={18} />, roles: ['OrgAdmin', 'SysAdmin'], section: 'admin', showForEventAdmin: true },
  // Hidden: under development
  // { path: '/admin/reports', labelKey: 'nav.reports', icon: <BarChart3 size={18} />, roles: ['OrgAdmin', 'SysAdmin'], section: 'admin' },
  { path: '/admin/users', labelKey: 'nav.userManagement', icon: <Users size={18} />, roles: ['SysAdmin'], section: 'sysadmin' },
  { path: '/admin/organizations', labelKey: 'nav.organizationManagement', icon: <Building2 size={18} />, roles: ['SysAdmin'], section: 'sysadmin' },
];

const BLIA_LOGO = '/blia_logo.png';

export function MainLayout({ children, hideMobileNav = false }: MainLayoutProps) {
  const { user, logout, isLoading } = useAuth();
  const { t, language, setLanguage } = useI18n();
  const { breadcrumbs } = useBreadcrumb();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  const toggleLanguage = () => {
    setLanguage(language === 'zh-TW' ? 'en' : 'zh-TW');
  };

  const visibleNavItems = navItems.filter(item => {
    if (!item.roles) return true;
    if (!user) return false;
    // Show for event admins even if their role doesn't match
    if (item.showForEventAdmin && user.isEventAdmin) return true;
    if (!item.roles.includes(user.role)) return false;
    return true;
  });

  const mainNavItems = visibleNavItems.filter(item => item.section === 'main');
  const adminNavItems = visibleNavItems.filter(item => item.section === 'admin');
  const sysadminNavItems = visibleNavItems.filter(item => item.section === 'sysadmin');

  // Build mobile nav items: main items + Association Registrations if user has access
  const mobileNavItems = [
    ...mainNavItems.filter(item => item.path !== '/settings'), // Dashboard, Events, My Registrations
    ...adminNavItems.filter(item => item.path === '/orgadmin/registrations'), // Association Registrations (if visible)
    ...mainNavItems.filter(item => item.path === '/settings'), // Settings always last
  ];

  // Get current page info for breadcrumb
  const getCurrentPageInfo = () => {
    const currentItem = visibleNavItems.find(
      item => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
    );
    return currentItem ? t(currentItem.labelKey) : '';
  };

  if (isLoading) {
    return (
      <div className="layout-loading">
        <div className="spinner" />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="app-shell">
      {/* Top Header Bar */}
      <header className="top-bar">
        <div className="top-bar-left">
          {/* Mobile Menu Toggle */}
          <button
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Logo & App Name */}
          <Link to="/" className="app-brand">
            <img src={BLIA_LOGO} alt="BLIA" className="app-logo" />
            <span className="app-name">{t('app.name')}</span>
          </Link>

          {/* Breadcrumb (desktop) */}
          <div className="top-breadcrumb">
            <ChevronRight size={14} />
            {breadcrumbs.length > 0 ? (
              breadcrumbs.map((item, index) => (
                <span key={index} className="breadcrumb-item">
                  {index > 0 && <ChevronRight size={14} />}
                  {item.path ? (
                    <Link to={item.path}>{item.label}</Link>
                  ) : (
                    <span>{item.label}</span>
                  )}
                </span>
              ))
            ) : (
              <span>{getCurrentPageInfo()}</span>
            )}
          </div>
        </div>

        <div className="top-bar-right">
          {/* User Info - Clickable link to profile */}
          {user && (
            <Link to="/profile" className="top-user-link">
              <User size={16} />
              <span className="top-user-name">
                {user.displayName || user.email}
              </span>
            </Link>
          )}

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="top-lang-btn"
            title={t('common.switchLanguage')}
          >
            <Globe size={16} />
            <span>{language === 'zh-TW' ? 'EN' : '中文'}</span>
          </button>

          {/* Logout */}
          {user && (
            <button
              onClick={handleLogout}
              className="top-logout-btn"
              title={t('auth.logout')}
            >
              <LogOut size={18} />
            </button>
          )}
        </div>
      </header>

      {/* Main Area (Sidebar + Content) */}
      <div className="main-area">
        {/* Sidebar Overlay (mobile) */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar Navigation */}
        <nav className={`side-nav ${sidebarOpen ? 'open' : ''}`}>
          {/* Main Navigation */}
          <div className="nav-group">
            <div className="nav-group-label">{t('common.menu') || 'Menu'}</div>
            <ul className="nav-items">
              {mainNavItems.map(item => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`nav-item ${location.pathname === item.path || location.pathname.startsWith(item.path + '/') ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="nav-item-icon">{item.icon}</span>
                    <span className="nav-item-label">{t(item.labelKey)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Admin Navigation */}
          {adminNavItems.length > 0 && (
            <div className="nav-group">
              <div className="nav-group-label">{t('common.orgManagement') || 'Org Management'}</div>
              <ul className="nav-items">
                {adminNavItems.map(item => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`nav-item ${location.pathname === item.path || location.pathname.startsWith(item.path + '/') ? 'active' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="nav-item-icon">{item.icon}</span>
                      <span className="nav-item-label">{t(item.labelKey)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* System Admin Navigation */}
          {sysadminNavItems.length > 0 && (
            <div className="nav-group">
              <div className="nav-group-label">{t('common.sysAdmin') || 'System Admin'}</div>
              <ul className="nav-items">
                {sysadminNavItems.map(item => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`nav-item ${location.pathname === item.path || location.pathname.startsWith(item.path + '/') ? 'active' : ''}`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="nav-item-icon">{item.icon}</span>
                      <span className="nav-item-label">{t(item.labelKey)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </nav>

        {/* Page Content */}
        <main className={`page-content${hideMobileNav ? ' page-content--no-mobile-nav' : ''}`}>
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      {!hideMobileNav && (
        <nav className="mobile-bottom-nav">
          {mobileNavItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-nav-btn ${location.pathname === item.path || location.pathname.startsWith(item.path + '/') ? 'active' : ''}`}
            >
              <span className="mobile-nav-btn-icon">{item.icon}</span>
              <span className="mobile-nav-btn-label">{t(item.labelKey)}</span>
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}

export default MainLayout;
